// src/task-centre/TaskList.jsx
import React from "react";
import Button from "../ui/Button";
import PageState from "../ui/PageState";
import Table from "../ui/Table";
import StatusBadge from "../ui/StatusBadge";
import { colors, spacing, typography } from "../ui/tokens";
import { sectionHeaderStyle, toolbarStyle } from "../ui/pagePatterns";

const shellStyle = {
  height: "100%",
  minHeight: 0,
  display: "grid",
  gridTemplateRows: "auto auto minmax(0, 1fr)",
  gap: spacing.md,
};

const headerStyle = {
  ...sectionHeaderStyle,
  alignItems: "flex-start",
  paddingBottom: spacing.sm,
  borderBottom: `1px solid ${colors.border}`,
};

const titleBlockStyle = {
  display: "grid",
  gap: spacing.xs,
};

const titleStyle = {
  margin: 0,
  color: colors.text,
  fontSize: typography.size.base,
  fontWeight: typography.weight.bold,
};

const subtitleStyle = {
  margin: 0,
  color: colors.textMuted,
  fontSize: typography.size.xs,
};

const headerActionsStyle = {
  ...toolbarStyle,
  justifyContent: "flex-end",
};

const countStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

const badgeRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.sm,
};

const tableWrapStyle = {
  minHeight: 0,
  overflow: "auto",
};

const titleCellStyle = {
  display: "grid",
  gap: spacing.xs / 2,
};

const titleTextStyle = {
  margin: 0,
  fontWeight: typography.weight.medium,
  color: colors.text,
  lineHeight: typography.lineHeight.form,
  wordBreak: "break-word",
};

const metaTextStyle = {
  color: colors.textMuted,
  fontSize: typography.size.xs,
};

const dueTextStyle = {
  color: colors.text,
};

const tableCss = `
  .task-list-root .task-list-row {
    cursor: pointer;
  }
  .task-list-root .task-list-row td {
    transition: background-color 120ms ease;
  }
  .task-list-root .task-list-row:hover td {
    background: ${colors.surfaceSubtle};
  }
  .task-list-root .task-list-row:focus-visible td {
    background: ${colors.accentSubtle};
    outline: 1px solid ${colors.accentBorder};
    outline-offset: -1px;
  }
`;

function computeSummary(tasks) {
  const today = new Date();
  const oneWeek = 7 * 24 * 60 * 60 * 1000;

  let overdue = 0;
  let dueSoon = 0;
  let upcoming = 0;

  tasks.forEach((t) => {
    if (!t.dueDate) {
      return;
    }
    const due = new Date(t.dueDate);
    if (Number.isNaN(due.getTime())) {
      return;
    }
    if (due < today) overdue++;
    else if (due < new Date(today.getTime() + oneWeek)) dueSoon++;
    else upcoming++;
  });

  return { overdue, dueSoon, upcoming };
}

const formatDueCell = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  const now = new Date();
  const isOverdue = parsed < now;
  return (
    <span
      style={isOverdue ? { ...dueTextStyle, color: colors.danger, fontWeight: typography.weight.medium } : dueTextStyle}
    >
      {parsed.toLocaleDateString()}
    </span>
  );
};

export default function TaskList({ tasks, loading, error, onTaskClick, onCreateTask, onRetry }) {
  const summary = computeSummary(tasks);
  const columns = [
    {
      id: "title",
      header: "Title",
      accessor: (row) => (
        <div style={titleCellStyle}>
          <p style={titleTextStyle}>{row.title || "Untitled task"}</p>
          {row.description ? <span style={metaTextStyle}>{row.description}</span> : null}
        </div>
      ),
      sortable: false,
      width: "36%",
    },
    {
      id: "controlId",
      header: "Control",
      accessor: (row) => <span style={metaTextStyle}>{row.controlId || "-"}</span>,
      sortable: false,
      width: "14%",
    },
    {
      id: "framework",
      header: "Framework",
      accessor: (row) => <span style={metaTextStyle}>{row.framework || "-"}</span>,
      sortable: false,
      width: "12%",
    },
    {
      id: "status",
      header: "Status",
      accessor: (row) => <StatusBadge variant="status" value={row.status} fallbackLabel={row.status || "Unknown"} />,
      sortable: false,
      width: "14%",
    },
    {
      id: "priority",
      header: "Priority",
      accessor: (row) => <StatusBadge variant="severity" value={row.priority} fallbackLabel={row.priority || "Unknown"} />,
      sortable: false,
      width: "12%",
    },
    {
      id: "due",
      header: "Due",
      accessor: (row) => formatDueCell(row.dueDate),
      sortable: false,
      width: "12%",
    },
  ];

  return (
    <div className="task-list-root" style={shellStyle}>
      <style>{tableCss}</style>
      <div style={headerStyle}>
        <div style={titleBlockStyle}>
          <h3 style={titleStyle}>My Tasks</h3>
          <p style={subtitleStyle}>Track ownership, due dates, and execution status in one place.</p>
        </div>
        <div style={headerActionsStyle}>
          <div style={countStyle}>
            {tasks.length} task{tasks.length !== 1 ? "s" : ""}
          </div>
          <Button
            disabled={!onCreateTask} onClick={() => onCreateTask && onCreateTask()}
            variant="primary"
          >
            + New Task
          </Button>
        </div>
      </div>

      <div style={badgeRowStyle}>
        <StatusBadge variant="status" value="overdue" label={`Overdue: ${summary.overdue}`} />
        <StatusBadge variant="status" value="due soon" label={`Due this week: ${summary.dueSoon}`} />
        <StatusBadge variant="status" value="upcoming" label={`Upcoming: ${summary.upcoming}`} />
      </div>

      {error ? (
        <PageState
          state="error"
          align="left"
          title="Tasks are unavailable"
          description={error}
          action={
            onRetry ? (
              <Button type="button" variant="secondary" onClick={() => onRetry()}>
                Refresh
              </Button>
            ) : null
          }
        />
      ) : loading ? (
        <PageState
          state="loading"
          align="left"
          title="Loading tasks"
          description="Glyder is preparing the current task queue and due-date summary."
        />
      ) : tasks.length === 0 ? (
        <PageState
          state="empty"
          align="left"
          title="No tasks match the current filters"
          description="Adjust the filters or create a new task to start tracking follow-up work."
          action={
            <Button type="button" variant="primary" disabled={!onCreateTask} onClick={() => onCreateTask && onCreateTask()}>
              + New Task
            </Button>
          }
        />
      ) : (
        <div style={tableWrapStyle}>
          <Table
            columns={columns}
            data={tasks}
            onRowClick={(row) => onTaskClick && onTaskClick(row.id)}
            getRowKey={(row) => row.id}
            getRowProps={() => ({
              className: "task-list-row",
            })}
            emptyState="No tasks match your filters."
          />
        </div>
      )}
    </div>
  );
}
